import { CUSTOMER_API_BASE_URL } from '../config/env';
import type { SceneCapabilityStates, SceneConfig } from '../types/sceneConfig';
import type {
  ViewerAgentChatRequest,
  ViewerAgentChatResponse,
  ViewerAgentSceneCapabilities,
} from '../types/viewerAgent';

const STADIUM_ZEMUN_PINS = {
  totalSeats: 4633,
  sections: Array.from({ length: 108 }, (_, i) => `section ${String(i + 1).padStart(2, '0')}`),
  directions: ['west', 'south-east', 'north-east', 'south-west', 'south', 'north-west', 'north', 'east'],
  colors: ['mint', 'light blue / blue', 'light blue', 'green', 'mint / white / pale light blue', 'dark blue'],
  seatsByDirection: { west: 1180, 'south-east': 1115, 'north-east': 991, 'south-west': 872, south: 269, 'north-west': 105, north: 72, east: 29 },
  seatsByColor: { mint: 1114, 'light blue / blue': 1035, 'light blue': 754, green: 697, 'mint / white / pale light blue': 659, 'dark blue': 374 },
};

/** Pre-built page URLs keyed by sceneId substring (case-insensitive). First match wins. */
const FLOOR_PLAN_URL_PATTERNS: Array<{ match: string; url: string }> = [
  { match: 'construction', url: '/floor-plans/kimbark.html' },
];

/** Pre-built progress report URLs keyed by sceneId substring (case-insensitive). First match wins. */
const PROGRESS_REPORT_URL_PATTERNS: Array<{ match: string; url: string }> = [
  { match: 'construction', url: '/reports/kimbark-progress.html' },
];

export function buildViewerAgentCapabilities(
  scene: SceneConfig,
  capabilityStates: SceneCapabilityStates
): ViewerAgentSceneCapabilities {
  const sid = scene.sceneId.toLowerCase();
  const floorPlanUrl = FLOOR_PLAN_URL_PATTERNS.find(p => sid.includes(p.match))?.url;
  const progressReportUrl = PROGRESS_REPORT_URL_PATTERNS.find(p => sid.includes(p.match))?.url;
  return {
    sceneId: scene.sceneId,
    reconstructionId: scene.reconstructionId,
    siSessionName: scene.siSessionName,
    siSessionNames: scene.siSessionNames,
    siContextNote: scene.siContextNote,
    floorPlanUrl,
    progressReportUrl,
    hasManifest: !!scene.manifest,
    hasGaus: !!scene.gaus,
    hasTimeLapse: !!capabilityStates.hasTimeLapse,
    hasImagePoses: !!scene.imagePoses,
    hasGeoAlignment: !!scene.hasGeoAlignment,
    hasPathNetwork: !!scene.pathNetworkUrl,
    hasImageBasePath: !!scene.imageBasePath,
    supportsImageView: capabilityStates.imageView.state === 'AVAILABLE_NOW',
    supportsAerialFlythrough: capabilityStates.supportsAerialFlythrough,
    supportsOrbitFlythrough: capabilityStates.supportsOrbitFlythrough,
    supportsPoseWalkthrough: capabilityStates.supportsPoseWalkthrough,
    // Temporary until viewer canvas recording is wired to backend persistence.
    supportsRecording: false,
    supportsCoordinateConversion: capabilityStates.supportsCoordinateConversion,
    ...(scene.navigableRooms ? { navigableRooms: scene.navigableRooms } : {}),
    ...(sid === 'stadium_zemun' ? { stadiumPins: STADIUM_ZEMUN_PINS } : {}),
    metadata: {
      category: scene.category,
      sceneType: scene.sceneType,
      reconstructionId: scene.reconstructionId,
    },
  };
}

export async function sendViewerAgentMessage(
  request: ViewerAgentChatRequest
): Promise<ViewerAgentChatResponse> {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/viewer-agent/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Viewer agent request failed: ${response.status}${errorText ? ` ${errorText}` : ''}`);
  }

  return response.json() as Promise<ViewerAgentChatResponse>;
}

export async function generateViewerReportPdf(input: {
  template: File;
  sceneName: string;
  title: string;
  sourceText: string;
}): Promise<Blob> {
  const body = new FormData();
  body.append('template', input.template);
  body.append('sceneName', input.sceneName);
  body.append('title', input.title);
  body.append('sourceText', input.sourceText);
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/viewer-agent/reports/generate`, { method: 'POST', body });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Report generation failed: ${response.status}${detail ? ` ${detail}` : ''}`);
  }
  return response.blob();
}

export type ViewerAgentStreamEvent =
  | { type: 'status'; content: string }
  | {
      type: 'spatialIntelligence';
      event:
        | { type: 'status'; sessionId?: string; content: string }
        | { type: 'text'; sessionId: string; content: string }
        | { type: 'tool'; sessionId: string; name: string; input?: Record<string, unknown> }
        | { type: 'result'; sessionId: string; files: string[] }
        | { type: 'warning'; sessionId: string; content: string }
        | { type: 'raw'; sessionId: string; phase: 'welcome' | 'response'; eventType: string; event: unknown; raw: string }
        | { type: 'done'; sessionId: string };
    }
  // ── New SI delegation event types ──────────────────────────────────────────
  | { type: 'delegate'; target: 'spatialIntelligence'; visibility: 'customer' }
  | { type: 'si_text'; text: string; sessionId?: string; visibility: 'customer' }
  | { type: 'si_tool_start'; toolName: string; sessionId?: string; visibility: 'debug' }
  | { type: 'si_result'; files: string[]; sessionId?: string; visibility: 'customer' }
  | { type: 'si_warning'; message: string; sessionId?: string; visibility: 'customer' }
  | { type: 'si_done'; sessionId?: string; visibility: 'customer' }
  | { type: 'si_raw'; phase: 'welcome' | 'response'; eventType: string; raw: string; sessionId?: string; visibility: 'debug' }
  | { type: 'si_error'; message: string; visibility: 'customer' }
  | { type: 'si_pending_task'; originalQuestion: string; visibility: 'customer' }
  | { type: 'final'; response: ViewerAgentChatResponse }
  | { type: 'error'; content: string };

export async function sendViewerAgentMessageStream(
  request: ViewerAgentChatRequest,
  onEvent: (event: ViewerAgentStreamEvent) => void,
  signal?: AbortSignal,
): Promise<ViewerAgentChatResponse> {
  const response = await fetch(`${CUSTOMER_API_BASE_URL}/api/viewer-agent/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/x-ndjson',
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok || !response.body) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Viewer agent stream failed: ${response.status}${errorText ? ` ${errorText}` : ''}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResponse: ViewerAgentChatResponse | null = null;
  const counters = {
    siSessionId: undefined as string | undefined,
    totalEvents: 0,
    statusEvents: 0,
    spatialEvents: 0,
    spatialTextEvents: 0,
    spatialToolEvents: 0,
    spatialResultEvents: 0,
    spatialWarningEvents: 0,
    spatialRawEvents: 0,
    spatialDoneEvents: 0,
    spatialTextChars: 0,
  };

  const recordEvent = (event: ViewerAgentStreamEvent) => {
    counters.totalEvents += 1;
    if (event.type === 'status') counters.statusEvents += 1;
    if (event.type === 'spatialIntelligence') {
      counters.spatialEvents += 1;
      counters.siSessionId = event.event.sessionId ?? counters.siSessionId;
      if (event.event.type === 'text') {
        counters.spatialTextEvents += 1;
        counters.spatialTextChars += event.event.content.length;
      } else if (event.event.type === 'tool') {
        counters.spatialToolEvents += 1;
      } else if (event.event.type === 'result') {
        counters.spatialResultEvents += 1;
      } else if (event.event.type === 'warning') {
        counters.spatialWarningEvents += 1;
      } else if (event.event.type === 'raw') {
        counters.spatialRawEvents += 1;
      } else if (event.event.type === 'done') {
        counters.spatialDoneEvents += 1;
      }
    }
    // New SI delegation event types
    if (event.type === 'si_text') {
      counters.spatialEvents += 1;
      counters.spatialTextEvents += 1;
      counters.spatialTextChars += event.text.length;
      counters.siSessionId = event.sessionId ?? counters.siSessionId;
    } else if (event.type === 'si_tool_start') {
      counters.spatialEvents += 1;
      counters.spatialToolEvents += 1;
    } else if (event.type === 'si_result') {
      counters.spatialEvents += 1;
      counters.spatialResultEvents += 1;
    } else if (event.type === 'si_warning') {
      counters.spatialEvents += 1;
      counters.spatialWarningEvents += 1;
    } else if (event.type === 'si_done') {
      counters.spatialEvents += 1;
      counters.spatialDoneEvents += 1;
      counters.siSessionId = event.sessionId ?? counters.siSessionId;
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const event = JSON.parse(trimmed) as ViewerAgentStreamEvent;
      recordEvent(event);
      onEvent(event);
      if (event.type === 'final') finalResponse = event.response;
      if (event.type === 'error') {
        console.log('[ViewerAgentStream] error', counters);
        throw new Error(event.content);
      }
    }
  }

  if (buffer.trim()) {
    const event = JSON.parse(buffer.trim()) as ViewerAgentStreamEvent;
    recordEvent(event);
    onEvent(event);
    if (event.type === 'final') finalResponse = event.response;
    if (event.type === 'error') {
      console.log('[ViewerAgentStream] error', counters);
      throw new Error(event.content);
    }
  }

  console.log('[ViewerAgentStream] complete', counters);

  if (!finalResponse) throw new Error('Viewer agent stream ended without a final response');
  return finalResponse;
}
